import * as React from 'react';
import DateFnsUtils from '@date-io/date-fns';
import MomentUtils from '@date-io/moment';
import {InvariantError} from 'ts-invariant';
import {Moment} from 'moment';
import * as R from 'ramda';
import {ThemeOptions, ThemeProvider} from '@mui/material/styles';
import {AdapterDateFns} from '@mui/x-date-pickers/AdapterDateFns';
import {AdapterMoment} from '@mui/x-date-pickers/AdapterMoment';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {DesktopDatePicker} from '@mui/x-date-pickers/DesktopDatePicker';
import TextField, {TextFieldProps} from '@mui/material/TextField';
import {isNotNil} from '@practicegenius/pg-util/src/TypescriptUtils';
import {MuiSxProps} from '../utils/sx-helpers';

type Props<T = Date | Moment> = Omit<
  React.ComponentProps<typeof DesktopDatePicker>,
  'error' | 'onChange' | 'renderInput'
> & {
  error?: string | null;
  hasError?: boolean;
  showKeyboardIcon?: boolean;
  onChange: (date: T | null, value?: string | null | undefined) => void;
  name?: string;
  id?: string;
  /** `helperText` prop passed to underlying TextField. Overridden by error message if it exists */
  helperText?: string;
  /** `sx` prop passed to underlying TextField */
  inputStyle?: MuiSxProps;
  /** `placeholder` prop passed to underlying TextField */
  placeholder?: string;
  /** `onBlur` prop passed to underlying TextField */
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  /** `onFocus` prop passed to underlying TextField */
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  /** `required` prop passed to underlying TextField */
  required?: boolean;
  variant?: TextFieldProps['variant'];
};

type MomentPickerProps = {inputType: 'moment'} & Props<Moment>;
type DatePickerProps = {inputType: 'date'} & Props<Date>;

function getHasError(props: Props): boolean {
  const {error, hasError = Boolean(error)} = props;
  return hasError;
}

/**
 * Use to override Calendar CSS for DatePicker.
 * See https://material-ui-pickers.dev/guides/css-overrides.
 */
const datePickerTheme: ThemeOptions = {
  components: {
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&:focus': {backgroundColor: 'rgba(0, 0, 0, 0.12)'},
        },
      },
    },
  },
};

/**
 * Generic DateInput that wraps DatePicker from @mui/x-date-pickers/DatePicker
 * At runtime, the value is determined by which provider you plugin to the surrounding `ThemeProvider.dateUtils`
 * For writing code that deals with either type concretely, you can specify either `Date` or `Moment` as the generic parameter
 * to get accurate typechecking in `props.onChange`
 */
export function DateInput(
  props: MomentPickerProps | DatePickerProps
): React.ReactElement {
  const {
    inputType,
    error,
    hasError,
    helperText,
    value,
    id,
    name,
    onChange,
    showKeyboardIcon,
    inputStyle = {},
    placeholder,
    required = false,
    onBlur,
    onFocus,
    variant,
    PopperProps,
    ...otherProps
  } = props;

  const fieldId = isNotNil(id) ? id : `${name}_dateinput`;

  // Oh how we all long for the day that Moment is dead. Because we can't
  // tell **what** a `null` is, we have to always explicitly set the format
  // to `undefined` until we get started.
  const {adapter, pickerUtils, datePickerFormat} = (() => {
    switch (inputType) {
      case 'date':
        return {
          adapter: AdapterDateFns,
          pickerUtils: DateFnsUtils,
          datePickerFormat: 'MM/dd/yyyy',
        };

      case 'moment':
        return {
          adapter: AdapterMoment,
          pickerUtils: MomentUtils,
          datePickerFormat: 'MM/DD/YYYY',
        };

      default:
        throw new InvariantError(`Unknown input type ${inputType}`);
    }
  })();

  return (
    <ThemeProvider theme={R.mergeDeepLeft(datePickerTheme)}>
      <LocalizationProvider dateAdapter={adapter} utils={pickerUtils}>
        <DesktopDatePicker
          label={props.label}
          inputFormat={datePickerFormat}
          mask="__/__/____"
          value={value ?? ''}
          // @ts-ignore
          onChange={onChange}
          disableOpenPicker={!showKeyboardIcon}
          PopperProps={PopperProps}
          renderInput={renderInputProps => {
            // @ts-ignore
            const fieldProps: React.ComponentProps<typeof TextField> = {
              ...renderInputProps,
              id: fieldId,
              // @ts-ignore
              error: getHasError(props),
              helperText: error || helperText,
              fullWidth: true,
              inputProps: {
                ...(renderInputProps.inputProps ?? {}),
                'data-testid': 'DatePicker__input',
                placeholder:
                  placeholder ?? renderInputProps.inputProps?.placeholder,
              },
              sx: {
                // Per product spec: most date inputs will have this specific static width unless we decide to override it
                width: '160px',
                ...inputStyle,
              },
              required,
              onBlur,
              onFocus,
            };
            return (
              <TextField
                margin="dense"
                size="small"
                {...fieldProps}
                variant={variant}
              />
            );
          }}
          {...otherProps}
        />
      </LocalizationProvider>
    </ThemeProvider>
  );
}
